import * as Api from '@ViewModels';

/**
 * The term is escaped into a Mongo regex server-side, which fails the whole request rather than matching
 * nothing once the pattern grows too large. Comfortably longer than any subject line, so a real search is
 * never truncated. Lives here rather than with the input because the URL can carry a term the box never saw.
 */
export const SEARCH_TERM_MAX_LENGTH = 200;

/**
 * The report searches for the trimmed, clamped term, so every path into the filter has to agree on it.
 * Trimming again after the clamp keeps this idempotent: without it a term whose cut lands on a space
 * normalises to a different value on the next pass, and the URL is rewritten twice instead of once.
 */
export const normalizeSearchTerm = (value: string | null): string | null =>
	value?.trim().slice(0, SEARCH_TERM_MAX_LENGTH).trim() || null;

/** Which sends a tab reports on. Every other criterion narrows within this. */
type BulkEmailReportScope = Api.BulkEmailFilterProperty.IndividualSends | Api.BulkEmailFilterProperty.OnlyCampaigns;

export function buildBulkEmailReportFilter({
	scope,
	searchTerm,
	selectedEmailCategories,
	selectedSendStatusList,
	selectedUserId,
}: {
	scope: BulkEmailReportScope;
	searchTerm?: string;
	selectedEmailCategories?: Api.EmailReportingCategory[];
	selectedSendStatusList: Api.EmailSendStatus[];
	selectedUserId?: string;
}): Api.IFilterCriteria<Api.BulkEmailFilterProperty> {
	const criteria: Api.IFilterCriteria<Api.BulkEmailFilterProperty>[] = [{ property: scope }];

	if (selectedUserId) {
		criteria.push({ property: Api.BulkEmailFilterProperty.User, value: selectedUserId });
	}

	if (selectedEmailCategories?.length) {
		criteria.push({
			criteria: selectedEmailCategories.map(categoryOrProperty =>
				categoryOrProperty === Api.BulkEmailFilterProperty.OtherIndividualSends
					? { property: Api.BulkEmailFilterProperty.OtherIndividualSends }
					: { property: Api.BulkEmailFilterProperty.Category, value: categoryOrProperty }
			),
			op: Api.FilterOperator.Or,
		});
	}

	criteria.push({
		criteria: selectedSendStatusList.map(status => ({
			property: Api.BulkEmailFilterProperty.Status,
			value: status,
		})),
		op: Api.FilterOperator.Or,
	});

	if (searchTerm) {
		criteria.push({ property: Api.BulkEmailFilterProperty.Subject, value: searchTerm });
	}

	return { criteria, op: Api.FilterOperator.And };
}
